import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Clock, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

// Simulated API call - replace with your actual API endpoint
const fetchBlogPosts = async () => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  return [
    {
      id: 'why-microgreens',
      title: "Why Microgreens?",
      author: "Folsom Microgreens Team",
      date: "Nov 15, 2024",
      description: "Microgreens are tiny plants with a big nutritional punch, offering concentrated vitamins, minerals, and antioxidants that surpass their mature counterparts. ",
      content: "In this article, we explore what makes microgreens so unique, how they compare to sprouts and fully grown vegetables, and why they’re a must-have addition to your diet. Discover the science behind their nutritional power and learn how these vibrant greens can elevate both your meals and your health!",
      image: "images/why-microgreens.png"
    },
    {
      id: 'early-growth-stages',
      title: "Discover the Early Growth Stages of a Plant",
      author: "Folsom Microgreens Team",
      date: "Dec 15, 2024",
      description: "Have you ever wondered how the tiniest plants can pack the biggest punch of flavor and nutrients?",
      content: " At Folsom Microgreens, we believe that understanding the early growth stages of plants unlocks the secret to their nutritional power. In this blog, we delve into the fascinating journey of a plant’s growth, from seed germination to the emergence of true leaves, and explore why microgreens are considered a superfood. Whether you’re a home gardener, a health enthusiast, or simply curious about adding fresh flavors to your meals, this guide will inspire you to embrace the vibrant world of microgreens.",
      image: "images/discover_early_growth_stages.jpeg"
    },
    // {
    //   id: 'seed-to-table',
    //   title: "From Seed to Table: Our Growing Process",
    //   author: "Folsom Microgreens Team",
    //   date: "March 25, 2024",
    //   description: "Take a behind-the-scenes look at how we grow our premium microgreens.",
    //   content: "Every batch of microgreens starts with carefully selected, high-quality seeds...",
    //   image: "/api/placeholder/800/400"
    // }
  ];
};

function About() {
  const navigate = useNavigate();
  const [blogArticles, setBlogArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBlogPosts = async () => {
      const posts = await fetchBlogPosts();
      setBlogArticles(posts);
      setLoading(false);
    };
    loadBlogPosts();
  }, []);

  const handleReadMore = (articleId) => {
    navigate(`/blog/${articleId}`);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="container"
    >
      <Helmet>
        <title>Folsom Microgreens - About Us</title>
        <link rel="canonical" href="https://www.folsommicrogreens.com/#/about" />
        <meta name="description" content="Learn more about Folsom Microgreens, our mission, and our commitment to sustainable farming." />
        <meta name="keywords" content="about us, Folsom Microgreens, microgreens, sustainable farming" />
      </Helmet>

      {/* About Section */}
      <section className="about">
        <h1>About Us</h1>
        <div className="about-content">
          <p>
            At <strong>Folsom Microgreens</strong>, we're passionate about bringing the freshest, healthiest, and most vibrant microgreens straight from our home to yours. We believe that what we consume directly impacts our well-being, which is why we are dedicated to growing microgreens locally, right here in our own backyard.
          </p>
          <p>
            Our journey began with a simple idea:
            by cultivating microgreens at home, we ensure that each batch is handpicked, pesticide-free, and packed with the vitamins, minerals, and antioxidants that your body needs.
        Our microgreens are grown with love and care, ensuring you receive the freshest produce possible.
          </p>
          <p>
            Whether you're looking to add a burst of flavor to your meals, enhance your health, or simply enjoy the beauty of fresh greens, we are here to provide you with the finest microgreens. Join us in our mission to grow green, eat clean, and live well.
          </p>
        </div>
      </section>

      {/* Blog Articles Section */}
      <section className="blog">
        <h2>Latest From Our Blog</h2>
        {loading ? (
          <div className="loading">Loading blog posts...</div>
        ) : (
          <div className="blog-grid">
            {blogArticles.map((article) => (
              <motion.div
                key={article.id}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="blog-card">
                  <div className="blog-image">
                    <img 
                      src={article.image} 
                      alt={article.title}
                      className="blog-img" 
                    />
                  </div>
                  <CardHeader>
                    <CardTitle>{article.title}</CardTitle>
                    <CardDescription>
                      <div className="blog-meta">
                        <div className="meta-item">
                          <User className="icon" />
                          {article.author}
                        </div>
                        <div className="meta-item">
                          <Clock className="icon" />
                          {article.date}
                        </div>
                      </div>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="blog-desc">{article.description}</p>
                    <p className="blog-content">{article.content}</p>
                    <div className="card-actions">
                      <Button 
                        variant="outline" 
                        className="read-more"
                        onClick={() => handleReadMore(article.id)}
                      >
                        Read More
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      <style jsx>{`
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .loading {
          text-align: center;
          padding: 40px;
          font-size: 18px;
          color: #666;
        }

        .about {
          margin-bottom: 60px;
        }

        .about h1 {
          font-size: 32px;
          margin-bottom: 24px;
        }

        .about-content p {
          margin-bottom: 16px;
        }

        .blog h2 {
          font-size: 28px;
          margin-bottom: 32px;
        }

        .blog-grid {
          display: grid;
          gap: 32px;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        }

        .blog-card {
          border: 1px solid #e5e7eb;
          transition: box-shadow 0.3s ease;
        }

        .blog-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .blog-image {
          width: 100%;
          height: 200px;
          overflow: hidden;
        }

        .blog-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .blog-meta {
          display: flex;
          gap: 16px;
          margin-top: 8px;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .icon {
          width: 16px;
          height: 16px;
        }

        .blog-desc {
          margin-bottom: 16px;
        }

        .blog-content {
          margin-bottom: 16px;
        }

        .card-actions {
          display: flex;
          justify-content: flex-start;
          gap: 12px;
        }

        .read-more {
          width: fit-content;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          background: linear-gradient(135deg, #22c55e, #16a34a);
          color: white;
          padding: 1rem 2rem;
          border-radius: 9999px;
          font-weight: 600;
          transition: transform 0.2s;
        }

        @media (max-width: 768px) {
          .blog-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </motion.div>
  );
}

export default About;